/**
 * An app's landing page: what a connection-gated app does in Amoreg, and the
 * way in. Shown in place of the app until the person connects it (appGate),
 * as one full page like the launcher; the same page, with the connection in
 * place of the call to connect, is the app's About page once connected.
 *
 * The app brings its own actions (connect, its status) and its access note;
 * the name, tagline and capabilities come from its definition (appRegistry).
 *
 * @module components/apps/AppLandingPage
 */
import { Link } from "@tanstack/react-router";
import {
  BriefcaseIcon,
  ChevronRightIcon,
  FilePenLineIcon,
  FileStackIcon,
  MessageSquareTextIcon,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import type { AppLanding, AppLandingIcon } from "./appRegistry";

const CAPABILITY_ICONS: Readonly<Record<AppLandingIcon, LucideIcon>> = {
  workspaces: BriefcaseIcon,
  preview: FileStackIcon,
  chat: MessageSquareTextIcon,
  review: FilePenLineIcon,
};

export function AppLandingPage({
  appId,
  name,
  landing,
  mark,
  orgSegment,
  standalone,
  actions,
  access,
}: {
  readonly appId: string;
  readonly name: string;
  readonly landing: AppLanding;
  /** The app's tile, as the launcher shows it. */
  readonly mark: ReactNode;
  readonly orgSegment: string;
  /**
   * True on the landing itself, which has no area sidebar: it carries its own
   * way back to Apps. False on the About page, inside the area.
   */
  readonly standalone: boolean;
  /** The call to connect, or the connection once connected. */
  readonly actions: ReactNode;
  /** What access the app asks for, in a sentence or two. */
  readonly access: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-app-landing={appId}>
      {standalone ? (
        <header className="shrink-0" data-app-landing-header="">
          <nav
            aria-label="Breadcrumb"
            className={cn(
              "flex h-[52px] items-center gap-1 px-4 text-[13px] sm:px-5",
              COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
            )}
          >
            <Link
              to="/$org/apps"
              params={{ org: orgSegment }}
              data-app-landing-back=""
              className="rounded-md px-1 py-0.5 text-muted-foreground outline-hidden transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              Apps
            </Link>
            <ChevronRightIcon aria-hidden className="size-3.5 text-muted-foreground/60" />
            <span className="truncate px-1 text-foreground">{name}</span>
          </nav>
        </header>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div
          className={cn(
            "mx-auto w-full max-w-2xl px-6 pb-16",
            standalone ? "pt-8 sm:pt-14" : "pt-10",
          )}
        >
          <div className="flex items-center gap-4">
            {mark}
            <div className="min-w-0">
              <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-foreground">{name}</h1>
              <p className="mt-0.5 text-sm text-pretty text-muted-foreground">{landing.tagline}</p>
            </div>
          </div>

          <div className="mt-7" data-app-landing-actions="">
            {actions}
          </div>

          <section aria-labelledby={`${appId}-landing-capabilities`} className="mt-10">
            <h2
              id={`${appId}-landing-capabilities`}
              className="text-xs font-medium text-muted-foreground"
            >
              In Amoreg
            </h2>
            <ul className="mt-2.5 divide-y divide-border/60 rounded-xl border border-border/70">
              {landing.capabilities.map((capability) => {
                const Icon = CAPABILITY_ICONS[capability.icon];
                return (
                  <li
                    key={capability.title}
                    className="flex gap-3.5 px-4 py-3.5"
                    data-app-landing-capability={capability.icon}
                  >
                    <span
                      aria-hidden
                      className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"
                    >
                      <Icon className="size-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium text-foreground">
                        {capability.title}
                      </span>
                      <span className="mt-0.5 block text-[13px] text-pretty text-muted-foreground">
                        {capability.body}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section aria-labelledby={`${appId}-landing-access`} className="mt-8">
            <h2 id={`${appId}-landing-access`} className="text-xs font-medium text-muted-foreground">
              Access
            </h2>
            <div className="mt-2 space-y-1.5 text-[13px] text-pretty text-muted-foreground">
              {access}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
